import { CacheKey } from '../types/cache-keys';
import { ApprovalRequestSource, ApprovalRequestType } from '../types/approval-request';
import { fetchPov } from '../components/Opportunity/opportunityDetailHelpers';
import { fetchItem, fetchItems } from './queries';
import { ensureQueryData } from '../hooks/base-hooks';
import { PovTeamMember } from '../types/opportunity-pov-items';
import { titleCase } from '../components/security-toolbox-ui/security-toolbox-ui-utils';

interface SourceFailure {
  key: string;
  reason: 'error' | 'unsupported_source' | 'unsupported_template';
  message: string;
  status?: number;
  code?: string;
}

interface SourceResolution {
  key: string;
  data: any;
  failedSource?: SourceFailure;
}

interface ApprovalRequestSourceData {
  sourceData: Record<string, any>;
  failedSources: SourceFailure[];
}

const normalizeSourceError = (error: unknown) => {
  const fallback = {
    message: 'Failed to load source data',
    status: undefined,
    code: undefined,
  } as { message: string; status?: number; code?: string };

  if (!error || typeof error !== 'object') {
    return fallback;
  }

  const sourceError = error as { message?: string; status?: number; code?: string };
  return {
    message: sourceError.message || fallback.message,
    status: typeof sourceError.status === 'number' ? sourceError.status : undefined,
    code: typeof sourceError.code === 'string' ? sourceError.code : undefined,
  };
};

export const sourceFunctions: Record<ApprovalRequestType, Record<string, (rootObjectIds: Record<ApprovalRequestSource, string | number>) => Promise<any>>> = {
  [ApprovalRequestType.PoV]: {
    pov: async (rootObjectIds) => {
      if (!rootObjectIds[ApprovalRequestSource.Opportunity]) return null;
      return ensureQueryData([CacheKey.Pov, rootObjectIds[ApprovalRequestSource.Opportunity]], () =>
        fetchPov(rootObjectIds[ApprovalRequestSource.Opportunity] as string)
      );
    },

    pov_team: async (rootObjectIds) => {
      if (!rootObjectIds[ApprovalRequestSource.PoV] || !rootObjectIds[ApprovalRequestSource.Opportunity]) return null;
      const povTeam = await ensureQueryData(
        [CacheKey.PovTeam, rootObjectIds[ApprovalRequestSource.PoV]],
        () => fetchItems('db/pov-team', { pov: rootObjectIds[ApprovalRequestSource.PoV] })
      );
      const team = povTeam[0];

      const opportunity = await ensureQueryData(
        [CacheKey.Opportunity, rootObjectIds[ApprovalRequestSource.Opportunity]],
        () => fetchItem(`sf/opportunity/${rootObjectIds[ApprovalRequestSource.Opportunity]}`)
      );
      const salesforceId = opportunity?.AccountId;

      let sfTeam = [];
      if (salesforceId) {
        sfTeam = await ensureQueryData(
          [CacheKey.SalesforceCustomerTeam, salesforceId],
          () => fetchItems('sf/query', { q: `SELECT Id, FirstName, LastName FROM Contact WHERE AccountId = '${salesforceId}'` })
        );
      }

      const members = sfTeam.reduce((acc, member) => {
        acc[member.Id] = member;
        return acc;
      }, {} as { [key: string]: PovTeamMember });

      return {
        ...team,
        cust_bdm: members[team?.cust_bdm] ? `${members[team?.cust_bdm].FirstName} ${members[team?.cust_bdm].LastName}` : '',
        cust_tdm: members[team?.cust_tdm] ? `${members[team?.cust_tdm].FirstName} ${members[team?.cust_tdm].LastName}` : '',
        cust_champ: members[team?.cust_champ] ? `${members[team?.cust_champ].FirstName} ${members[team?.cust_champ].LastName}` : '',
      };
    },

    opportunity_product: async (rootObjectIds) => {
      if (!rootObjectIds[ApprovalRequestSource.Opportunity]) return null;
      const products = await ensureQueryData(
        [CacheKey.OpportunityProductAssignment, rootObjectIds[ApprovalRequestSource.Opportunity]],
        () => fetchItems('db/opportunity-product', { opportunity: rootObjectIds[ApprovalRequestSource.Opportunity] })
      );
      return products.find((p) => p.id === rootObjectIds[ApprovalRequestSource.OpportunityProduct]) ?? null;
    },

    pov_outcome: async (rootObjectIds) => {
      if (!rootObjectIds[ApprovalRequestSource.PoV] || !rootObjectIds[ApprovalRequestSource.OpportunityProduct]) return [];
      const outcomes = await ensureQueryData(
        [CacheKey.PovOutcomes, rootObjectIds[ApprovalRequestSource.PoV], rootObjectIds[ApprovalRequestSource.OpportunityProduct]],
        () => fetchItems(`db/pov/${rootObjectIds[ApprovalRequestSource.PoV]}/product/${rootObjectIds[ApprovalRequestSource.OpportunityProduct]}`)
      );
      return outcomes;
    },

    pov_prereq: async (rootObjectIds) => {
      if (!rootObjectIds[ApprovalRequestSource.PoV] || !rootObjectIds[ApprovalRequestSource.OpportunityProduct]) return [];
      const prereqs = await ensureQueryData(
        [CacheKey.PovPrereqs, rootObjectIds[ApprovalRequestSource.PoV]],
        () => fetchItems('db/pov-prereq', { pov: rootObjectIds[ApprovalRequestSource.PoV] })
      );
      return prereqs.filter((p) => p.opportunity_product === rootObjectIds[ApprovalRequestSource.OpportunityProduct]);
    },
  },
  [ApprovalRequestType.Escalation]: {
    opportunity: async (rootObjectIds) => {
      if (!rootObjectIds[ApprovalRequestSource.Opportunity]) return null;
      const opportunity = await ensureQueryData(
        [CacheKey.Opportunity, rootObjectIds[ApprovalRequestSource.Opportunity]],
        () => fetchItem(`sf/opportunity/${rootObjectIds[ApprovalRequestSource.Opportunity]}`)
      );

      return {
        ...opportunity,
        highest_se_phase: opportunity?.highest_se_phase ? titleCase(opportunity.highest_se_phase) : 'Discovery',
      };
    },
  },
};

export const getApprovalRequestDataViaSource = async (
  approvalType: ApprovalRequestType,
  sourceKeys: string[],
  rootObjectIds
): Promise<ApprovalRequestSourceData> => {
  if (!sourceFunctions[approvalType]) {
    const message = `No source keys defined for approval type: ${approvalType}`;
    console.warn(message);
    return {
      sourceData: {},
      failedSources: [{
        key: approvalType,
        reason: 'unsupported_template',
        message: `unsupported template type: ${approvalType}`,
      }],
    };
  }
  const resolvedSources: SourceResolution[] = await Promise.all(
    sourceKeys.map(async (key) => {
      const func = sourceFunctions[approvalType][key];

      if (!func) {
        const message = `No function found for key: ${key}`;
        console.warn(message);
        return {
          key,
          data: null,
          failedSource: {
            key,
            reason: 'unsupported_source',
            message,
          },
        };
      }

      try {
        const data = await func(rootObjectIds);
        return { key, data };
      } catch (error) {
        const normalizedError = normalizeSourceError(error);
        return {
          key,
          data: null,
          failedSource: {
            key,
            reason: 'error',
            ...normalizedError,
          },
        };
      }
    })
  );

  return {
    sourceData: Object.fromEntries(resolvedSources.map(({ key, data }) => [key, data])),
    failedSources: resolvedSources
      .map(({ failedSource }) => failedSource)
      .filter((failedSource): failedSource is SourceFailure => !!failedSource),
  };
};
